import { CookieSettingsValue } from '@/cookies';
import { AlertType } from '@/shared/ui/alert';

type EventTypePayload = {
    'router:navigate': {
        url: string;
    };
    'router:back': {};
    'settings:change-preferred-language': {};
    'settings:change-preferred-currency': {};
    'settings:change-preferred-venue': {};
    'account:view-detail': {};
    'account:auth:view-detail': {};
    'account:auth:register': {};
    'account:auth:login': {};
    'account:auth:logout': {};
    'account:data:update': {};
    'account:address:create': {};
    'account:address:update': {};
    'account:address:delete': {};
    'account:coupons:view-detail': {};
    'account:coupons:apply': {
        code: string;
    };
    'account:order:recreate': {};
    'account:order:download': {};
    'account:favorite-items:view-detail': {};
    'account:product:add-to-favorite': {
        id: string;
    };
    'account:product:remove-from-favorite': {
        id: string;
    };
    'account:review:send': {};
    'product:view-detail': {
        slug: string;
        variantHash?: string;
    };
    'product:addons-menu:view-detail': {
        productHash: string;
        initialQuantity?: number;
        initialVariant?: string;
    };
    'product-menu:view-detail': {};
    'cart:view-detail': {};
    'cart:product:add-to-cart':
        | {
              id?: string;
              productHash: string;
              quantity: number;
              variant?: string;
              addons?: Record<string, string | Record<string, number>>;
              memberVariants?: Record<string, string>;
              note?: string;
              suppressModal?: boolean;
              skipAgeVerification?: boolean;
          }
        | {
              items: Array<{
                  id?: string;
                  productHash: string;
                  quantity: number;
                  variant?: string;
                  addons?: Record<string, string | Record<string, number>>;
                  memberVariants?: Record<string, string>;
                  note?: string;
              }>;
              suppressModal?: boolean;
              skipAgeVerification?: boolean;
          };
    'cart:product:change-quantity': {
        id: string;
        quantity: number;
    };
    'cart:product:remove-from-cart': {
        id: string;
    };
    // 'cart:form:select-bonus-points-amount': {};
    // 'cart:form:select-delivery-method': {};
    // 'cart:form:select-top-up': {};
    // 'cart:form:select-payment-method': {};
    // 'cart:form:select-preferred-time': {};
    // 'cart:form:select-tip': {};
    'phone-verification:start': {
        telephone: string;
        nextRetryAt?: string;
        onVerify?(): void;
        fallbackToLocal?: boolean;
    };
    'phone-verification:finish': {
        code: string;
        modalId?: number;
    };
    // 'cart:form:update-customer-data': {};
    'cart:form:submit': {};
    'order:update': {};
    'order:view-alert-tracking': {
        orderId: string;
    };
    'order:view-map-tracking': {};
    'cookies:view-detail': {};
    'cookies:update-consents': {
        data: CookieSettingsValue;
    };
    'contact:view-detail': {};
    'contact:delivery-areas:view-detail': {};
    'notifications:show': {
        type?: AlertType | `${AlertType}`;
        title: string;
        description?: string;
    };
    'notifications:update': {};
    'notifications:hide': {};
    'privacy-policy:view-detail': {
        onAccept?(): void;
    };
    'terms-and-conditions:view-detail': {
        onAccept?(): void;
    };
    'register-terms-and-conditions:view-detail': {
        onAccept?(): void;
    };
};

type EmptyPayloadEvents = {
    [K in keyof EventTypePayload]: EventTypePayload[K] extends Record<string, never> ? K : never;
}[keyof EventTypePayload];

export class EventService {
    static trigger(event: EmptyPayloadEvents, payload?: never): void;
    static trigger<T extends Exclude<keyof EventTypePayload, EmptyPayloadEvents>>(
        event: T,
        payload: EventTypePayload[T],
    ): void;
    static trigger<T extends keyof EventTypePayload>(event: T, payload?: EventTypePayload[T]): void {
        window.dispatchEvent(new CustomEvent(event, { detail: payload }));
    }

    static on(
        event: EmptyPayloadEvents,
        callback: (event: CustomEvent<Record<string, never>>) => void,
    ): () => void;
    static on<T extends Exclude<keyof EventTypePayload, EmptyPayloadEvents>>(
        event: T,
        callback: (event: CustomEvent<EventTypePayload[T]>) => void,
    ): () => void;
    static on<T extends keyof EventTypePayload>(
        event: T,
        callback: (event: CustomEvent<EventTypePayload[T]>) => void,
    ): () => void {
        const propagatedCallback = ((e: CustomEvent<EventTypePayload[T]>) => {
            callback(e);
        }) as EventListener;
        window.addEventListener(event, propagatedCallback);

        return () => window.removeEventListener(event, propagatedCallback);
    }
}
